import { axiosArisChatInstance } from "../AxiosConfiguration";

/**
 * Dialogue Overview — the commitment page payload.
 *
 * Direct to aris-ai, not through core-lambda: the detail lives on aris-ai and the
 * proxy would add a network leg without adding anything. Same pattern as
 * Services/faithProfile.js, which reaches `/ARIS/v2/faith-profile` directly.
 *
 * The **v4** spelling, not `/ARIS/dialogues/{id}`. That v1 route is frozen by the
 * contract-freeze suite (AV-06 / plans/20 N1: "no existing endpoint changes shape,
 * status codes or semantics"), so the EX-07 commitment block — experience_class,
 * primary_action, secondary_actions, eligibility, current_focus — is served here
 * instead. Both halves arrive in one call; the page needs them together to decide
 * which primary action to draw.
 *
 * Response: { success, data: { ...v1 detail fields, experience_class,
 * primary_action, secondary_actions, eligibility, current_focus } }
 */
export const getDialogueDetail = async (templateId) => {
  const res = await axiosArisChatInstance.get(
    `/ARIS/v4/explore/dialogues/${encodeURIComponent(templateId)}/overview`
  );
  return res?.data?.data ?? res?.data ?? null;
};

/**
 * `POST /ARIS/dialogues/{template_id}/start` — create or resume the member's
 * instance. Fast (no AI call). Keep `instance_id` and `has_opened`; Aris opens
 * the session with the chat `open` event, so `welcome_text` is always null and
 * `session_index` / `thread_id` are display-only — never send them back.
 */
export const startDialogue = async (templateId, payload = {}) => {
  const res = await axiosArisChatInstance.post(
    `/ARIS/dialogues/${encodeURIComponent(templateId)}/start`,
    payload
  );
  return res?.data?.data ?? res?.data ?? null;
};

/**
 * `GET /ARIS/v4/dialogues/instances/{instance_id}/messages?before=&limit=` —
 * one page of the Dialogue's whole history, every session and both modalities,
 * **newest first**: `{instance_id, messages: [{id, role, content, modality,
 * session_index, timestamp, voice_session_id}], next_before,
 * current_session_index, thread_id, has_opened}`. Page older with
 * `before = next_before` until it is null.
 */
export const getDialogueMessages = async (instanceId, { before, limit = 50 } = {}) => {
  const res = await axiosArisChatInstance.get(
    `/ARIS/v4/dialogues/instances/${encodeURIComponent(instanceId)}/messages`,
    { params: { limit, ...(before ? { before } : {}) } }
  );
  return res?.data?.data ?? res?.data ?? null;
};

/**
 * The Dialogue's full history, oldest first, de-duplicated on `id`. Walks
 * `next_before` page by page (capped so a runaway cursor can't loop forever).
 * `has_opened` comes from the newest page; `isComplete` is true only when the
 * server explicitly sends `current_session_index: null` (view-only Dialogue).
 */
export const getDialogueHistory = async (instanceId, { limit = 100, maxPages = 20 } = {}) => {
  const seen = new Set();
  const newestFirst = [];
  let before;
  let head = null;
  for (let page = 0; page < maxPages; page += 1) {
    const data = await getDialogueMessages(instanceId, { before, limit });
    if (!head) head = data;
    for (const m of data?.messages || []) {
      if (m?.id && seen.has(m.id)) continue;
      if (m?.id) seen.add(m.id);
      newestFirst.push(m);
    }
    before = data?.next_before;
    if (!before) break;
  }
  return {
    messages: newestFirst.reverse(),
    hasOpened: Boolean(head?.has_opened),
    isComplete: Boolean(head) && "current_session_index" in head && head.current_session_index === null,
  };
};

/**
 * Lifecycle moves on one of the member's own instances (UD v26.5 §20):
 * `POST /ARIS/v4/dialogues/instances/{instance_id}/pause | resume | archive`
 * → `{success, data: {instance_id, state, previous_state, status}}`.
 *
 * - **409** — the move is illegal from the current state; the body is
 *   `detail: {message, state}`. The caller refetches and renders the server's
 *   state; it never retries or guesses.
 * - **404** — not the member's instance, or `lifecycle_v26_5` is off for them.
 *   The caller hides the lifecycle actions.
 *
 * Archive is final: ARCHIVED is terminal and there is no restore.
 */
const lifecycleMove = async (instanceId, move) => {
  const res = await axiosArisChatInstance.post(
    `/ARIS/v4/dialogues/instances/${encodeURIComponent(instanceId)}/${move}`
  );
  return res?.data?.data ?? res?.data ?? null;
};

export const pauseDialogue = (instanceId) => lifecycleMove(instanceId, "pause");
export const resumeDialogue = (instanceId) => lifecycleMove(instanceId, "resume");
export const archiveDialogue = (instanceId) => lifecycleMove(instanceId, "archive");

/** HTTP status of a failed lifecycle call, or null for a network failure. */
export const lifecycleErrorStatus = (err) => err?.response?.status ?? null;

/** The server's current state from a 409 body (`detail.state`), when it sent one. */
export const lifecycleConflictState = (err) =>
  err?.response?.status === 409 ? err?.response?.data?.detail?.state ?? null : null;

/** The member-facing instance states the list groups by. */
export const DIALOGUE_LIST_STATES = ["ACTIVE", "PAUSED", "COMPLETED", "ARCHIVED"];

/**
 * `GET /ARIS/v4/dialogues/instances?state=…&limit=…` — the member's own
 * Dialogues in one state, with the counts for every tab:
 * `{state, counts: {ACTIVE, PAUSED, COMPLETED, ARCHIVED}, items: [{
 * dialogue_instance_id, dialogue_id, title, state, image_url,
 * display_area_key, last_activity_at, paused_at, completed_at, archived_at}]}`.
 * Items arrive most recent first by the tab's own date; render in order.
 */
export const listMyDialogues = async (state = "ACTIVE", limit = 50) => {
  const res = await axiosArisChatInstance.get("/ARIS/v4/dialogues/instances", {
    params: { state, limit },
  });
  return res?.data?.data ?? res?.data ?? null;
};
